/**
 * 류 단계의 우 detail — 그 류의 모든 호 popum row 표시.
 *
 * Props:
 *   chapter: "42" (2-digit)
 *
 * 화면 (image 211 패턴):
 *   | 호 | 품명 한글 | 품명 영문 |
 *   | 4201 (yellow) | 동물용 마구... | Saddlery and harness... |
 *   | 4202 (yellow) | 트렁크·슈트 케이스... | Trunks, suit-cases... |
 *   ...
 *
 * 행 클릭 → URL ?hs=NNNN 으로 navigate (호 단계 hierarchy 진입).
 *
 * Fetch: /api/onto/chapter-headings?chapter=NN — popum_ko / popum_en 전체 본문.
 * stripLeadingDotCode 로 "42.02 - " prefix 제거.
 */
import React, { useState, useEffect, useMemo } from "react";

function stripLeadingDotCode(text) {
  if (!text) return "";
  return text.replace(/^[\d.]+\s*[-–]\s*/, "");
}

function navigateToHs(hs4) {
  const u = new URL(window.location.href);
  u.searchParams.set("audit", "onto-detail");
  u.searchParams.set("hs", hs4);
  u.searchParams.delete("section");
  u.searchParams.delete("chapter");
  u.searchParams.delete("hsk");
  window.history.pushState({}, "", u.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export default function ChapterHeadingsGrid({ chapter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!chapter || !/^[0-9]{1,2}$/.test(chapter)) {
      setData({ headings: [] });
      setLoading(false);
      return;
    }
    const padded = String(chapter).padStart(2, "0");
    setLoading(true);
    setErr(null);
    fetch(`/api/onto/chapter-headings?chapter=${padded}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d || { headings: [] }))
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [chapter]);

  const headings = useMemo(() => data?.headings || [], [data]);

  if (loading) {
    return (
      <div
        style={{ color: "var(--audit-text-muted)", fontSize: 13, padding: 8 }}
      >
        호 목록 불러오는 중…
      </div>
    );
  }
  if (err) {
    return (
      <div
        style={{
          color: "#d93025",
          fontSize: 12,
          padding: 10,
          border: "1px solid #d93025",
          borderRadius: 4,
        }}
      >
        오류: {err}
      </div>
    );
  }
  if (headings.length === 0) {
    return (
      <div
        style={{
          padding: 16,
          color: "var(--audit-text-muted)",
          fontSize: 13,
          fontStyle: "italic",
          border: "1px dashed var(--audit-border)",
          borderRadius: 6,
          background: "var(--audit-bg-subtle)",
        }}
      >
        제 {parseInt(chapter, 10)} 류에 등록된 호 없음.
      </div>
    );
  }

  return (
    <table
      style={{
        width: "100%",
        fontSize: 12,
        borderCollapse: "collapse",
        background: "var(--audit-bg-elevated)",
        border: "1px solid var(--audit-border)",
        tableLayout: "fixed",
      }}
    >
      <colgroup>
        <col style={{ width: 70 }} />
        <col />
        <col />
      </colgroup>
      <thead>
        <tr style={{ background: "var(--audit-bg-subtle)" }}>
          <th rowSpan={2} style={thStyle}>
            호
          </th>
          <th colSpan={2} style={thStyle}>
            품명 (한국 2026년)
          </th>
        </tr>
        <tr style={{ background: "var(--audit-bg-subtle)" }}>
          <th
            style={{ ...thStyle, borderTop: "1px solid var(--audit-border)" }}
          >
            한글
          </th>
          <th
            style={{ ...thStyle, borderTop: "1px solid var(--audit-border)" }}
          >
            영문
          </th>
        </tr>
      </thead>
      <tbody>
        {headings.map(({ hs4, title_ko, title_en, popum_ko, popum_en }) => (
          <tr
            key={hs4}
            onClick={() => navigateToHs(hs4)}
            style={{
              borderTop: "1px solid var(--audit-border)",
              cursor: "pointer",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--audit-bg-hover)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "")}
          >
            <td style={hs4CellStyle}>{hs4}</td>
            <td style={koCellStyle}>
              {title_ko || stripLeadingDotCode(popum_ko)}
            </td>
            <td style={enCellStyle}>
              {title_en || stripLeadingDotCode(popum_en)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const thStyle = {
  textAlign: "center",
  padding: "6px 8px",
  borderBottom: "1px solid var(--audit-border-strong)",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--audit-text-muted)",
};

const hs4CellStyle = {
  padding: "10px 4px",
  textAlign: "center",
  verticalAlign: "top",
  background: "transparent",
  color: "var(--audit-text)",
  fontFamily: "JetBrains Mono, monospace",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.5px",
  borderRight: "1px solid var(--audit-border)",
};

const koCellStyle = {
  padding: "10px 12px",
  verticalAlign: "top",
  fontSize: 12,
  color: "var(--audit-text)",
  lineHeight: 1.65,
  whiteSpace: "normal",
  wordBreak: "keep-all",
  overflowWrap: "anywhere", // 한·영 혼합 긴 토큰이 좁은 셀(모바일)을 넘지 않도록
  borderRight: "1px solid var(--audit-border)",
};

const enCellStyle = {
  padding: "10px 12px",
  verticalAlign: "top",
  fontSize: 12,
  color: "var(--audit-text-muted)",
  lineHeight: 1.55,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
};
